import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MuiRangeSlider from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/RangeSlider';
import TextField from 'Components/UI/TextField';
import Typography from 'Components/UI/Typography';
import { withStyles } from '@material-ui/core/styles';
import { noop } from 'Helpers/PropTypes';

const styles = (theme) => ({
  root: {},
  inputs: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  input: { paddingBottom: 0 },
  innerLabel: {
    margin: theme.spacing(0, 1),
  },
});

const RangeSlider = ({
  label,
  classes,
  minValue,
  maxValue,
  minBound,
  maxBound,
  onMinUpdate,
  onMaxUpdate,
  InputProps,
  test,
}) => {
  const [lesser, setLesser] = useState(Math.min(Math.max(minBound, parseInt(minValue, 10)), maxBound));
  const [greater, setGreater] = useState(Math.max(minBound, Math.min(parseInt(maxValue, 10), maxBound)));

  const [lesserPending, setLesserPending] = useState(Math.min(Math.max(minBound, parseInt(minValue, 10)), maxBound));
  const [greaterPending, setGreaterPending] = useState(Math.max(minBound, Math.min(parseInt(maxValue, 10), maxBound)));

  const handleChange = (event, newValue) => {
    if (newValue?.length === 2) {
      const values = newValue.sort((a, b) => a - b);
      // Set all new values
      setLesser(values[0]);
      setLesserPending(values[0]);
      setGreater(values[1]);
      setGreaterPending(values[1]);
      onMinUpdate(values[0]);
      onMaxUpdate(values[1]);
    }
  };

  const handleBlur = () => {
    // Default to minValue to cover case where input has value of ''
    const values = [
      lesserPending || minBound,
      greaterPending || minBound,
    ].sort((a, b) => a - b);
    let l = values[0];
    let g = values[1];

    // Clamp values to min and max
    if (l < minBound) {
      l = minBound;
    }
    if (g > maxBound) {
      g = maxBound;
    }

    // Set all new values
    setLesser(l);
    setLesserPending(l);
    setGreater(g);
    setGreaterPending(g);
    onMinUpdate(l);
    onMaxUpdate(g);
  };

  const handleLesserInputChange = (event) => {
    if (event.target.value === '') {
      setLesserPending('');
    } else {
      const values = [Number(event.target.value), greater];
      setLesserPending(values[0]);
      setGreaterPending(values[1]);
    }
  };
  const handleGreaterInputChange = (event) => {
    if (event.target.value === '') {
      setGreaterPending('');
    } else {
      const values = [lesser, Number(event.target.value)];
      setLesserPending(values[0]);
      setGreaterPending(values[1]);
    }
  };

  return (
    <div className={classNames(classes.root, classes)}>
      <Typography
        color="primary"
        variant="h6"
      >
        {label}
      </Typography>
      <div className={classes.inputs}>
        <TextField
          test={`${test}-min-filter`}
          className={classes.input}
          value={lesserPending}
          variant="outlined"
          type="number"
          margin="dense"
          onChange={handleLesserInputChange}
          onBlur={handleBlur}
          inputProps={{
            min: minBound,
            max: maxBound,
            type: 'number',
            'aria-labelledby': 'range-slider-min',
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={InputProps}
        />
        <Typography variant="h6" color="blue" className={classes.innerLabel}>To</Typography>
        <TextField
          test={`${test}-max-filter`}
          className={classes.input}
          value={greaterPending}
          variant="outlined"
          type="number"
          margin="dense"
          onChange={handleGreaterInputChange}
          onBlur={handleBlur}
          inputProps={{
            min: minBound,
            max: maxBound,
            'aria-labelledby': 'range-slider-max',
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={InputProps}
        />
      </div>
      <MuiRangeSlider
        value={[lesser, greater]}
        min={minBound}
        max={maxBound}
        onChange={handleChange}
      />
    </div>
  );
};

RangeSlider.defaultProps = {
  label: '',
  classes: {},
  InputProps: {},
  minValue: '0',
  maxValue: '100',
  minBound: 0,
  maxBound: 100,
  onMinUpdate: noop,
  onMaxUpdate: noop,
  test: '',
};

RangeSlider.propTypes = {
  label: PropTypes.string,
  classes: PropTypes.object,
  minBound: PropTypes.number,
  maxBound: PropTypes.number,
  minValue: PropTypes.string,
  maxValue: PropTypes.string,
  onMinUpdate: PropTypes.func,
  onMaxUpdate: PropTypes.func,
  InputProps: PropTypes.object,
  test: PropTypes.string,
};

export default withStyles(styles)(RangeSlider);
